import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Layout, App as AntdApp } from 'antd';
import { Provider } from 'react-redux';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { getProfile } from './store/slices/authSlice';
import HomePage from './pages/HomePage';
import BrandsPage from './pages/BrandsPage';
import BrandDetailPage from './pages/BrandDetailPage';
import RecommendationsPage from './pages/RecommendationsPage';
import FavoritesPage from './pages/FavoritesPage';
import HistoryPage from './pages/HistoryPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import EditProfilePage from './pages/EditProfilePage';
import AddBrandPage from './pages/AddBrandPage';
import RateBrandPage from './pages/RateBrandPage';
import RankingsPage from './pages/RankingsPage';
import MyRatingsPage from './pages/MyRatingsPage';
import MyReviewsPage from './pages/MyReviewsPage';
import WorldMapPage from './pages/WorldMapPage';
import DashboardPage from './pages/DashboardPage';
import PurchaseChannelsPage from './pages/PurchaseChannelsPage';
import Navbar from './components/Navbar';
import SiteFooter from './components/SiteFooter';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import './App.css';
import { AppThemeProvider } from './theme/ThemeProvider';
import { HelmetProvider } from 'react-helmet-async';

const { Content, Footer } = Layout;

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, token, user, profileLoading } = useAppSelector(
    state => state.auth
  );

  useEffect(() => {
    // 只有在已认证、有token但没有用户信息且未在加载时才获取用户资料
    if (isAuthenticated && token && !user && !profileLoading) {
      dispatch(getProfile());
    }
  }, [isAuthenticated, token, user, profileLoading, dispatch]);

  return (
    <Router>
      <Layout style={{ minHeight: '100vh' }}>
        <Navbar />
        <Content>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/brands/add" element={<AddBrandPage />} />
            <Route path="/brands/:id" element={<BrandDetailPage />} />
            <Route path="/brands/:id/rate" element={<RateBrandPage />} />
            <Route path="/rankings" element={<RankingsPage />} />
            <Route path="/world-map" element={<WorldMapPage />} />
            <Route path="/purchase-channels" element={<PurchaseChannelsPage />} />
            <Route path="/my-ratings" element={<MyRatingsPage />} />
            <Route path="/my-reviews" element={<MyReviewsPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/recommendations" element={<RecommendationsPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/edit" element={<EditProfilePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Routes>
        </Content>
        <Footer style={{ padding: 0 }}>
          <SiteFooter />
        </Footer>
      </Layout>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppThemeProvider>
        <HelmetProvider>
          <AntdApp>
            <AppContent />
          </AntdApp>
        </HelmetProvider>
      </AppThemeProvider>
    </Provider>
  );
};

export default App;
